import React from "react";
import { BookOpen, ArrowUpRight, Check, Clock } from "lucide-react";
import Reveal from "./Reveal";
import { BOOKS } from "../../data/content";

export default function Books() {
  const book = BOOKS[0];
  return (
    <section id="books" className="relative z-10 py-20 md:py-36 border-t border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <div className="text-[0.7rem] uppercase tracking-[0.24em] text-amber mb-5">Author</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] mb-5">
            My <span className="text-amber">Books</span>
          </h2>
          <p className="text-ink-secondary leading-relaxed">
            Turning years of hands-on experience into practical guides you can actually use.
          </p>
        </Reveal>

        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
          {/* Cover */}
          <Reveal className="lg:col-span-5">
            <div className="group relative mx-auto w-fit [perspective:1400px]">
              <div className="absolute -inset-6 rounded-3xl bg-amber/15 blur-3xl opacity-60" />
              <div className="relative transition-transform duration-500 ease-out group-hover:[transform:rotateY(-12deg)_rotateX(4deg)] [transform-style:preserve-3d]">
                <img
                  src={book.cover}
                  alt={book.title}
                  data-testid="book-cover"
                  className="relative w-64 md:w-72 rounded-r-lg rounded-l-sm shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/10"
                />
                {/* spine highlight */}
                <div className="absolute left-0 top-0 h-full w-3 rounded-l-sm bg-gradient-to-r from-black/50 to-transparent" />
              </div>
            </div>
          </Reveal>

          {/* Details */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="flex items-center gap-2 text-amber mb-4">
              <BookOpen size={18} />
              <span className="text-[0.72rem] uppercase tracking-[0.18em]">{book.localTitle} · {book.year}</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-5">{book.title}</h3>
            <p className="text-ink-secondary leading-relaxed mb-7 max-w-xl">{book.desc}</p>

            <ul className="space-y-3 mb-8">
              {book.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-ink-secondary">
                  <span className="mt-1 h-5 w-5 shrink-0 rounded-full bg-amber/15 text-amber flex items-center justify-center">
                    <Check size={12} />
                  </span>
                  {h}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-3 mb-8">
              {book.languages.map((l) => {
                const available = l.status === "available";
                return (
                  <span
                    key={l.label}
                    data-testid={`book-lang-${l.label.toLowerCase()}`}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${
                      available
                        ? "border-amber/40 text-amber bg-amber/5"
                        : "border-white/12 text-ink-muted bg-surface"
                    }`}
                  >
                    {available ? <Check size={14} /> : <Clock size={14} />}
                    {l.label}
                    <span className="opacity-70">· {available ? "Available" : "Coming soon"}</span>
                  </span>
                );
              })}
            </div>

            <a
              href={book.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-amber px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-obsidian hover:bg-amber-hover transition-colors duration-300"
            >
              Read in Georgian
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
